import React, { useState, useEffect, useRef } from 'react'
import { api, formatCurrency, formatDate } from '../../utils/api'
import { addBookedTodayDeal } from './collectionsBookedToday'
import { getTrackingUrl, extractTrackingNumber } from '../../utils/tracking'
import { assignCarrier, parsePackageDetails, resolvedAddressFields } from '../../utils/carrierAssignment'
import CollectionGroupCard from './CollectionGroupCard.jsx'
import CustomerEmails from './CustomerEmails.jsx'
import CollectionAddressEditor from './CollectionAddressEditor.jsx'
import CollectionHoldControl from './CollectionHoldControl.jsx'
import CollectionMethodPicker from './CollectionMethodPicker.jsx'
import CollectionHistory from './CollectionHistory.jsx'
import WarehouseDropoffPanel from './WarehouseDropoffPanel.jsx'
import { CARRIERS, formatNumber } from './carriers.js'
import { groupByCollection } from '../../utils/collectionGrouping'
import OffOnStaging from '../OffOnStaging'
import DealLink from '../shared/DealLink.jsx'
import CustomerChangesPanel from './CustomerChangesPanel.jsx'
import CollectionParcels from './CollectionParcels.jsx'
import CollectionCancelSection from './CollectionCancelSection.jsx'
import PostageToCancel from './PostageToCancel.jsx'
import { useCollectionParcels } from './useCollectionParcels.js'

// Deal stages for collections
const STAGES = {
  AWAITING_SHIPPING_LABEL: '597442256',
  AWAITING_COLLECTION: '4367683815',
  SHIPPING_ARRANGED: 'presentationscheduled',
  QUOTE_SENT: 'qualifiedtobuy',
  PROBLEM_WITH_SHIPPING: '589142218'
}

// Address parsing moved server-side: Prism resolves every collection address
// into resolved_address, read here via resolvedAddressFields(shipment) from
// carrierAssignment (which keeps the one guarded raw fallback). The old local
// parseAddress + extractPostcodeFromAddress are gone (Phase 3, spec 20260902).

// Strip characters FedEx rejects in address/name fields (apostrophes, quotes, etc.)
function fedexSafe(str) {
  if (!str) return ''
  return str.replace(/[''"`\\]/g, '').replace(/[^\x20-\x7E]/g, '')
}

/**
 * Generate FedEx batch upload CSV and trigger download.
 */
function generateFedExCSV(shipment, contactInfo) {
  const pkg = parsePackageDetails(shipment.package_details)
  const addr = resolvedAddressFields(shipment)
  const postcode = addr.postcode
  const boxes = pkg?.boxes || [{ weight: 0, length: 0, width: 0, height: 0 }]
  const boxCount = boxes.length

  const HEADERS = [
    'serviceType','shipmentType','senderContactName','senderCompany','senderContactNumber',
    'senderLine1','senderLine2','senderLine3','senderPostcode','senderCity','senderCountry',
    'senderEmail','senderResidential','recipientContactName','recipientCompany',
    'recipientContactNumber','recipientLine1','recipientLine2','recipientPostcode',
    'recipientCity','recipientCountry','recipientEmail','numberOfPackages','packageWeight',
    'weightUnits','length','width','height','etdEnabled','packageType','currencyType',
    'commodityType','itemDescription','manufacturingCountry','commodityQuantity',
    'commodityMeasureUnit','commodityWeight','customsValue','purposeOfShipment','generateInvoice'
  ]

  const avgWeight = Math.round(boxes.reduce((s, b) => s + (b.weight || 0), 0) / boxCount)
  const avgLength = Math.round(boxes.reduce((s, b) => s + (b.length || 0), 0) / boxCount)
  const avgWidth = Math.round(boxes.reduce((s, b) => s + (b.width || 0), 0) / boxCount)
  const avgHeight = Math.round(boxes.reduce((s, b) => s + (b.height || 0), 0) / boxCount)

  const rows = [[
    'FEDEX_PRIORITY', 'OUTBOUND',
    fedexSafe(contactInfo.name), shipment.id, shipment.collection_phone || '',
    fedexSafe(addr.line1), fedexSafe(addr.line2), shipment.id,
    postcode.replace(/\s+/g, ''), fedexSafe(addr.city), 'GB',
    contactInfo.email || '', 'Y',
    'Audio Trader', 'Audio Trader', '+44 7470 463786',
    'Unit 36', '36 Eldon Way', 'BN177HE',
    'Littlehampton', 'GB', 'hello@audiotrader.co.uk',
    boxCount, avgWeight, 'KGS',
    avgLength, avgWidth, avgHeight,
    '', 'YOUR_PACKAGING', 'GBP',
    '', '', '', '', '', '', '', '', ''
  ]]

  const csvContent = [HEADERS, ...rows]
    .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `fedex-${shipment.id}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * Build FedEx CSV rows for a single shipment (no header).
 */
function buildFedExRows(shipment, contactName, contactEmail) {
  const pkg = parsePackageDetails(shipment.package_details)
  const addr = resolvedAddressFields(shipment)
  const postcode = addr.postcode
  const boxes = pkg?.boxes || [{ weight: 0, length: 0, width: 0, height: 0 }]
  const boxCount = boxes.length

  const avgWeight = Math.round(boxes.reduce((s, b) => s + (b.weight || 0), 0) / boxCount)
  const avgLength = Math.round(boxes.reduce((s, b) => s + (b.length || 0), 0) / boxCount)
  const avgWidth = Math.round(boxes.reduce((s, b) => s + (b.width || 0), 0) / boxCount)
  const avgHeight = Math.round(boxes.reduce((s, b) => s + (b.height || 0), 0) / boxCount)

  return [[
    'FEDEX_PRIORITY', 'OUTBOUND',
    fedexSafe(contactName), shipment.id, shipment.collection_phone || '',
    fedexSafe(addr.line1), fedexSafe(addr.line2), shipment.id,
    postcode.replace(/\s+/g, ''), fedexSafe(addr.city), 'GB',
    contactEmail || '', 'Y',
    'Audio Trader', 'Audio Trader', '+44 7470 463786',
    'Unit 36', '36 Eldon Way', 'BN177HE',
    'Littlehampton', 'GB', 'hello@audiotrader.co.uk',
    boxCount, avgWeight, 'KGS',
    avgLength, avgWidth, avgHeight,
    '', 'YOUR_PACKAGING', 'GBP',
    '', '', '', '', '', '', '', '', ''
  ]]
}

const FEDEX_HEADERS = [
  'serviceType','shipmentType','senderContactName','senderCompany','senderContactNumber',
  'senderLine1','senderLine2','senderLine3','senderPostcode','senderCity','senderCountry',
  'senderEmail','senderResidential','recipientContactName','recipientCompany',
  'recipientContactNumber','recipientLine1','recipientLine2','recipientPostcode',
  'recipientCity','recipientCountry','recipientEmail','numberOfPackages','packageWeight',
  'weightUnits','length','width','height','etdEnabled','packageType','currencyType',
  'commodityType','itemDescription','manufacturingCountry','commodityQuantity',
  'commodityMeasureUnit','commodityWeight','customsValue','purposeOfShipment','generateInvoice'
]

/**
 * Generate batch FedEx CSV for multiple shipments and trigger download.
 */
function generateBatchFedExCSV(shipments) {
  const allRows = []
  for (const s of shipments) {
    const name = s._contactName || s.customer_name || ''
    const email = s._contactEmail || s.seller_email || ''
    allRows.push(...buildFedExRows(s, name, email))
  }

  const csvContent = [FEDEX_HEADERS, ...allRows]
    .map(row => row.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
    .join('\n')

  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `fedex-batch-${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// =============================================================================
// COMPONENTS
// =============================================================================

function CarrierColumn({ carrierId, shipments, selectedId, onSelect, batchMode, batchLoading, selectedFedExIds, onToggleBatch, onToggleSelect, onSelectAll, onDeselectAll, onDownloadBatch, selectedBatchIds, batchActionLabel }) {
  const carrier = CARRIERS[carrierId]
  const isFedEx = carrierId === 'fedex'
  const isRoyalMail = carrierId === 'royal_mail'
  const hasBatch = isFedEx || isRoyalMail
  const batchIds = isFedEx ? selectedFedExIds : selectedBatchIds
  const selectedCount = hasBatch ? (batchIds?.size || 0) : 0

  // One rendered card per PHYSICAL pickup: quotes sharing a COL- code fold
  // into a group. Selection, batch checkboxes and the Select All comparator
  // stay per-quote — each quote is still its own CSV row / carrier booking.
  const groups = groupByCollection(shipments)

  return (
    <div style={{
      flex: '0 0 280px',
      background: 'var(--color-bg-secondary)',
      borderRadius: 'var(--radius)',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 280px)',
      overflow: 'hidden'
    }}>
      <div style={{
        padding: 'var(--space-3)',
        borderBottom: '1px solid var(--color-border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 12, height: 12, borderRadius: '50%',
              background: carrier.color
            }} />
            <span
              onClick={() => window.open(carrier.url, '_blank', 'width=1200,height=800')}
              style={{
                fontWeight: 600,
                color: 'inherit',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={e => e.target.style.textDecoration = 'underline'}
              onMouseLeave={e => e.target.style.textDecoration = 'none'}
              title={`Open ${carrier.name} shipping portal`}
            >
              {carrier.name}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {hasBatch && shipments.length > 0 && (
              <button
                onClick={onToggleBatch}
                style={{
                  background: batchMode ? carrier.color : 'transparent',
                  color: batchMode ? 'white' : 'var(--color-text-muted)',
                  border: batchMode ? `1px solid ${carrier.color}` : '1px solid var(--color-border)',
                  padding: '3px 8px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                Batch
              </button>
            )}
            <span style={{
              background: '#333',
              color: 'white',
              padding: '4px 10px',
              borderRadius: 12,
              fontSize: 13,
              fontWeight: 600
            }}>{groups.length}</span>
          </div>
        </div>
        {hasBatch && batchMode && (
          <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
            <button
              className="btn btn-xs"
              onClick={selectedCount === shipments.length ? onDeselectAll : onSelectAll}
              style={{ fontSize: 10 }}
            >
              {selectedCount === shipments.length ? 'Deselect All' : 'Select All'}
            </button>
            <button
              className="btn btn-xs btn-primary"
              onClick={onDownloadBatch}
              disabled={selectedCount === 0}
              style={{
                fontSize: 10,
                background: selectedCount > 0 ? carrier.color : undefined,
                opacity: selectedCount === 0 ? 0.4 : 1
              }}
            >
              {batchLoading ? 'Processing...' : `${batchActionLabel || 'Download CSV'} (${selectedCount})`}
            </button>
          </div>
        )}
      </div>
      <div style={{ padding: 'var(--space-2)', flex: 1, overflowY: 'auto' }}>
        {shipments.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>
            No shipments
          </div>
        ) : (
          groups.map(g => (
            <CollectionGroupCard
              key={g.code ?? g.deals[0].id}
              group={g}
              selectedId={selectedId}
              onSelect={onSelect}
              batchMode={hasBatch && batchMode}
              batchIds={batchIds}
              onToggleSelect={onToggleSelect}
            />
          ))
        )}
      </div>
    </div>
  )
}

function QuickCopyRow({ label, value, onCopy, copied, mono }) {
  if (!value) return null
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '10px 0',
      borderBottom: '1px solid var(--color-border)'
    }}>
      <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 500 }}>
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          fontSize: 13,
          fontWeight: mono ? 500 : 600,
          fontFamily: mono ? 'monospace' : 'inherit'
        }}>
          {value}
        </span>
        <button
          className="btn btn-xs"
          onClick={() => onCopy(value, label)}
          style={{
            fontSize: 10,
            padding: '4px 8px',
            background: '#333',
            color: 'white',
            border: 'none'
          }}
        >
          {copied === label ? '✓' : 'Copy'}
        </button>
      </div>
    </div>
  )
}

function DetailPanel({ shipment, onClose, onConfirm, onCarrierSaved, onHoldChanged, onMethodChanged, onCancelled }) {
  const parcelsState = useCollectionParcels(shipment.id)
  const [cancelOpen, setCancelOpen] = useState(false)
  const cancelSectionRef = useRef(null)
  const [trackingNumber, setTrackingNumber] = useState(shipment.tracking_number || '')
  const [shippingCost, setShippingCost] = useState(shipment.shipping_cost || '')
  const [collectionDate, setCollectionDate] = useState(shipment.collection_date || '')
  const [saving, setSaving] = useState(false)
  const [carrierSaved, setCarrierSaved] = useState(false)
  const [savingDate, setSavingDate] = useState(false)
  const [dateSaved, setDateSaved] = useState(false)
  const [copied, setCopied] = useState(null)

  // Contact info state - fetched separately to avoid subrequest limits
  const [contactInfo, setContactInfo] = useState({
    email: shipment.seller_email || null,
    name: shipment.contact_name || null
  })

  // Fetch contact info on mount
  useEffect(() => {
    async function fetchContact() {
      try {
        const res = await api.get(`/api/deals/collections/${shipment.id}/contact`)
        if (res.contact) {
          setContactInfo({
            email: res.contact.email || null,
            name: res.contact.firstName || null,
            contactId: res.contact.contactId
          })
        }
      } catch (e) {
        console.error('Failed to fetch contact:', e)
      }
    }
    if (!shipment.seller_email) {
      fetchContact()
    }
  }, [shipment.id])

  // FedEx label upload state
  const [labelFile, setLabelFile] = useState(null)
  const [uploadedLabelUrl, setUploadedLabelUrl] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)

  // Editable box details state
  const [editingBoxes, setEditingBoxes] = useState(false)
  const [editBoxes, setEditBoxes] = useState([])
  const [savingBoxes, setSavingBoxes] = useState(false)

  function startEditBoxes() {
    const parsed = parsePackageDetails(shipment.package_details)
    if (parsed?.boxes) {
      setEditBoxes(parsed.boxes.map(b => ({ ...b })))
    } else {
      setEditBoxes([{ boxNumber: 1, weight: 0, length: 0, width: 0, height: 0 }])
    }
    setEditingBoxes(true)
  }

  function updateBox(index, field, value) {
    setEditBoxes(prev => prev.map((b, i) => i === index ? { ...b, [field]: parseFloat(value) || 0 } : b))
  }

  function addBox() {
    setEditBoxes(prev => [...prev, { boxNumber: prev.length + 1, weight: 0, length: 0, width: 0, height: 0 }])
  }

  function removeBox(index) {
    setEditBoxes(prev => prev.filter((_, i) => i !== index).map((b, i) => ({ ...b, boxNumber: i + 1 })))
  }

  async function saveBoxes() {
    setSavingBoxes(true)
    try {
      const packageDetails = editBoxes.map(b =>
        `Box ${b.boxNumber}: ${b.weight}kg - ${b.length} x ${b.width} x ${b.height} cm`
      ).join('\n')
      await api.patch(`/api/deals/collections/${shipment.id}/package-details`, { package_details: packageDetails })
      shipment.package_details = packageDetails
      setEditingBoxes(false)
    } catch (e) {
      alert(`Failed to save: ${e.message}`)
    } finally {
      setSavingBoxes(false)
    }
  }

  // FedEx pickup scheduling state
  const [schedulingPickup, setSchedulingPickup] = useState(false)
  const [pickupResult, setPickupResult] = useState(null)

  // Royal Mail shipment state
  const [rmCreating, setRmCreating] = useState(false)
  const [rmShipment, setRmShipment] = useState(null)

  // Get auto-assigned carrier
  const autoAssigned = assignCarrier(shipment)

  // Use saved carrier from HubSpot if exists (case-insensitive), otherwise use auto-assigned
  const hubspotCarrier = shipment.carrier?.toLowerCase()
  const initialCarrier = hubspotCarrier && CARRIERS[hubspotCarrier]
    ? hubspotCarrier
    : autoAssigned.carrierId

  const [selectedCarrier, setSelectedCarrier] = useState(initialCarrier)
  const isManuallyChanged = selectedCarrier !== autoAssigned.carrierId
  const isFedEx = selectedCarrier === 'fedex'
  const isRoyalMail = selectedCarrier === 'royal_mail'

  const carrier = CARRIERS[selectedCarrier]
  const pkg = parsePackageDetails(shipment.package_details)
  const boxCount = pkg?.boxes?.length || 1
  const isDropOff = shipment.send_type === 'dropoff'

  // Label upload required for FedEx (always) or drop-offs (any carrier)
  const needsLabelUpload = isFedEx || isDropOff

  // A saved address edit mutates `shipment` in place; bump to re-render the panel
  // (the address editor is a child, so its own state change won't refresh us).
  const [, bumpAddress] = useState(0)
  // Downstream is committed once a label, pickup or tracking exists — a late
  // address edit then leaves those courier artifacts stale.
  const isBooked = !!(pickupResult || rmShipment || shipment.label_link || shipment.tracking_number)

  // On-Hold (spec 20260911): while held, every forward action is locked. The
  // hold control mutates `shipment` in place, so bump to re-render the panel.
  const [, bumpHold] = useState(0)
  const isOnHold = !!shipment.collection_on_hold

  // Check existing pickup status on mount for FedEx
  useEffect(() => {
    async function checkPickup() {
      try {
        const res = await api.get(`/api/deals/collections/${shipment.id}/pickup-status`)
        if (res.pickup) {
          setPickupResult({
            confirmationCode: res.pickup.confirmation_code,
            locationCode: res.pickup.location_code,
            collectionDate: res.pickup.collection_date,
            pickupWindow: res.pickup.pickup_window || '12:00 - 17:00'
          })
        }
      } catch (e) {
        // ignore
      }
    }
    if (isFedEx) checkPickup()
  }, [shipment.id, isFedEx])

  // Check existing Royal Mail shipment on mount
  useEffect(() => {
    async function checkRmShipment() {
      try {
        const res = await api.get(`/api/deals/collections/royal-mail/${shipment.id}/status`)
        if (res.shipment && res.shipment.status === 'created') {
          setRmShipment(res.shipment)
        }
      } catch (e) {
        // ignore
      }
    }
    if (isRoyalMail) checkRmShipment()
  }, [shipment.id, isRoyalMail])

  // Calculate minimum date (tomorrow)
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  const minDate = tomorrow.toISOString().split('T')[0]

  async function copyToClipboard(text, field) {
    await navigator.clipboard.writeText(text)
    setCopied(field)
    setTimeout(() => setCopied(null), 1500)
  }

  // Handle file selection for FedEx label
  function handleFileSelect(e) {
    const file = e.target.files[0]
    if (file) {
      setLabelFile(file)
      setUploadError(null)
    }
  }

  // Upload label to R2
  async function handleUploadLabel() {
    if (!labelFile) return
    setIsUploading(true)
    setUploadError(null)

    try {
      const formData = new FormData()
      formData.append('file', labelFile)
      formData.append('dealId', shipment.id)

      const apiBase = import.meta.env.VITE_API_URL || ''
      const response = await fetch(`${apiBase}/api/r2/upload-label`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      })

      const result = await response.json()
      if (result.success) {
        setUploadedLabelUrl(result.url)
        setLabelFile(null)
      } else {
        setUploadError(result.error || 'Upload failed')
      }
    } catch (err) {
      setUploadError('Upload failed: ' + err.message)
    } finally {
      setIsUploading(false)
    }
  }

  const dateDirty = !!collectionDate && collectionDate !== (shipment.collection_date || '')

  async function saveCollectionDate() {
    setSavingDate(true)
    try {
      await api.patch(`/api/deals/collections/${shipment.id}/collection-date`, { collection_date: collectionDate })
      shipment.collection_date = collectionDate
      setDateSaved(true)
      setTimeout(() => setDateSaved(false), 2500)
    } catch (e) {
      alert(`Failed to save collection date: ${e.message}`)
    } finally {
      setSavingDate(false)
    }
  }

  async function handleSaveCarrier() {
    // Save carrier only - no email, no stage change, deal stays on the board
    setSaving(true)
    try {
      await api.put(`/api/deals/collections/${shipment.id}/carrier`, {
        carrier: selectedCarrier,
        trackingNumber: trackingNumber || null,
        shippingCost: parseFloat(shippingCost) || 0,
        collectionDate: collectionDate || null,
        collectionAddress: shipment.collection_address,
        labelUrl: uploadedLabelUrl || null,
        boxCount,
        sendType: shipment.send_type,
        saveOnly: true
      })

      shipment.carrier = selectedCarrier
      if (collectionDate) shipment.collection_date = collectionDate
      onCarrierSaved?.(shipment.id, selectedCarrier)
      setCarrierSaved(true)
      setTimeout(() => setCarrierSaved(false), 2500)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSaving(false)
    }
  }

  async function handleConfirm() {
    // Validation: require label upload for FedEx or drop-offs
    if (needsLabelUpload && !uploadedLabelUrl) {
      alert(`Please upload the shipping label before confirming`)
      return
    }

    setSaving(true)
    try {
      // Update carrier info - backend now also updates stage and sends email automatically
      const result = await api.put(`/api/deals/collections/${shipment.id}/carrier`, {
        carrier: selectedCarrier,
        trackingNumber,
        shippingCost: parseFloat(shippingCost) || 0,
        collectionDate,
        collectionAddress: shipment.collection_address,
        labelUrl: uploadedLabelUrl,
        boxCount,
        sendType: shipment.send_type
      })

      // Warn user if email failed to send
      if (result.emailSent === false) {
        alert(`Warning: Collection booked but the customer email failed to send.${result.emailError ? `\n\nReason: ${result.emailError}` : ''}\n\nPlease send the email manually or retry.`)
      }

      onConfirm(selectedCarrier)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSaving(false)
    }
  }

  async function handleSchedulePickup() {
    if (!collectionDate) {
      alert('Please set a collection date first')
      return
    }
    const addr = resolvedAddressFields(shipment)
    const postcode = addr.postcode
    const pkg = parsePackageDetails(shipment.package_details)

    setSchedulingPickup(true)
    try {
      const result = await api.post(`/api/deals/collections/${shipment.id}/schedule-pickup`, {
        collectionDate,
        trackingNumber,
        address: { line1: addr.line1, line2: addr.line2 },
        contactName: contactInfo.name || 'Customer',
        phone: shipment.collection_phone || '',
        postcode: postcode || '',
        city: addr.city || '',
        totalWeight: pkg?.totalWeight || 0,
        packageCount: pkg?.boxes?.length || 1
      })
      setPickupResult(result)
    } catch (error) {
      alert(`Pickup scheduling failed: ${error.message}`)
    } finally {
      setSchedulingPickup(false)
    }
  }

  function openCancelSection() {
    setCancelOpen(true)
    // The section mounts on the next render, so scroll once it is there.
    setTimeout(() => cancelSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' }), 0)
  }

  const CopyRow = ({ label, value }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
      <div>
        <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>{label}</div>
        <div style={{ fontSize: 13 }}>{value || '-'}</div>
      </div>
      {value && (
        <button
          className="btn btn-xs"
          onClick={() => copyToClipboard(value, label)}
          style={{
            fontSize: 11,
            background: '#333',
            color: 'white',
            border: 'none',
            padding: '4px 8px'
          }}
        >
          {copied === label ? 'Copied!' : 'Copy'}
        </button>
      )}
    </div>
  )

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: 420,
      background: 'white',
      boxShadow: '-4px 0 20px rgba(0,0,0,0.15)',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header */}
      <div style={{ padding: 'var(--space-4)', background: carrier.color, color: 'white' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            {/* Carrier Selector */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
              <label htmlFor="collection-carrier" style={{ fontSize: 12, opacity: 0.85 }}>Carrier</label>
              <select
                id="collection-carrier"
                value={selectedCarrier}
                onChange={e => setSelectedCarrier(e.target.value)}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  minWidth: 140
                }}
              >
                {Object.values(CARRIERS).map(c => (
                  <option key={c.id} value={c.id} style={{ color: '#333' }}>
                    {c.name}{c.id === autoAssigned.carrierId ? ' (Auto)' : ''}
                  </option>
                ))}
              </select>
              {isManuallyChanged && (
                <button
                  onClick={() => setSelectedCarrier(autoAssigned.carrierId)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: 4,
                    fontSize: 11,
                    cursor: 'pointer'
                  }}
                >
                  Reset
                </button>
              )}
              <button
                onClick={handleSaveCarrier}
                disabled={saving || isOnHold}
                title={isOnHold ? 'Lift the hold to book' : undefined}
                style={{
                  background: 'rgba(255,255,255,0.25)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  color: 'white',
                  padding: '6px 12px',
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: (saving || isOnHold) ? 'not-allowed' : 'pointer',
                  opacity: (saving || isOnHold) ? 0.6 : 1
                }}
              >
                {saving ? 'Saving...' : carrierSaved ? 'Saved ✓' : 'Save Carrier'}
              </button>
            </div>
            {/* Status Tags */}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <CollectionMethodPicker
                shipment={shipment}
                isBooked={isBooked}
                isOnHold={isOnHold}
                onChanged={(sendType) => {
                  // The shipment was mutated in place; re-render this panel and
                  // tell the board so its lanes follow the new method.
                  bumpAddress(v => v + 1)
                  onMethodChanged?.(shipment.id, sendType)
                }}
              />
              {shipment.has_printer?.toLowerCase() === 'yes' && (
                <span style={{
                  background: 'rgba(255,255,255,0.3)',
                  padding: '4px 10px',
                  borderRadius: 4,
                  fontSize: 11,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4
                }}>
                  🖨️ Has Printer
                </span>
              )}
            </div>
            <div style={{ fontSize: 18, fontWeight: 600 }}>{shipment.dealname}</div>
            <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>
              <DealLink deal={shipment} className="deal-link--inherit" />
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', width: 32, height: 32, borderRadius: '50%', cursor: 'pointer', fontSize: 18 }}>
            ×
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4)' }}>
        {/* On-Hold control (spec 20260911) — pause a collection whose details
            the customer is still changing, so it cannot be booked wrong. */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <CollectionHoldControl
            shipment={shipment}
            onChanged={() => {
              bumpHold(v => v + 1)
              // Push the new hold state up so the board card's badge updates
              // now, not only on the next board reload (the shipment was
              // mutated in place, which alone would not re-render the board).
              onHoldChanged?.(shipment.id, !!shipment.collection_on_hold, shipment.collection_hold_reason ?? null)
            }}
          />
        </div>

        {/* Quick Copy Section */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>QUICK COPY</div>
          <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', padding: '4px 12px' }}>
            <QuickCopyRow label="Reference" value={shipment.reference_code || shipment.id} onCopy={copyToClipboard} copied={copied} mono />
            <QuickCopyRow label="Customer" value={contactInfo.name} onCopy={copyToClipboard} copied={copied} />
            <QuickCopyRow label="Phone" value={shipment.collection_phone} onCopy={copyToClipboard} copied={copied} mono />
            <QuickCopyRow label="Postcode" value={resolvedAddressFields(shipment).postcode} onCopy={copyToClipboard} copied={copied} mono />
            <QuickCopyRow label="Email" value={contactInfo.email} onCopy={copyToClipboard} copied={copied} />
          </div>
        </div>

        <CollectionHistory dealId={shipment.id} quoteId={shipment.quote_id} />

        {/* Customer emails (read-only) */}
        {contactInfo.email && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>CUSTOMER EMAILS</div>
            <CustomerEmails email={contactInfo.email} />
          </div>
        )}

        {/* Carrier Reason */}
        <div style={{ background: 'var(--color-bg-secondary)', padding: 'var(--space-3)', borderRadius: 'var(--radius)', marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
            {isManuallyChanged ? 'Manual Override' : 'Auto-Assigned Carrier'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
            {isManuallyChanged
              ? `Changed from ${CARRIERS[autoAssigned.carrierId]?.name || 'Unknown'} to ${carrier.name}`
              : autoAssigned.reason}
          </div>
        </div>

        {/* Address */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>
            {shipment.send_type === 'dropoff' ? 'DROP-OFF ADDRESS' : 'COLLECTION ADDRESS'}
          </div>
          <CollectionAddressEditor
            shipment={shipment}
            isBooked={isBooked}
            onSaved={() => bumpAddress(v => v + 1)}
          />
          <CopyRow label={shipment.send_type === 'dropoff' ? 'Drop-off Date' : 'Collection Date'} value={shipment.collection_date} />
        </div>

        {/* Package */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' }}>PACKAGE</div>
            {!editingBoxes && (
              <button
                className="btn btn-xs"
                onClick={startEditBoxes}
                style={{ fontSize: 10, padding: '2px 8px', background: '#333', color: 'white', border: 'none' }}
              >
                Edit
              </button>
            )}
          </div>
          {editingBoxes ? (
            <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 'var(--radius)', padding: 'var(--space-2)' }}>
              {editBoxes.map((box, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 6, fontSize: 11 }}>
                  <span style={{ color: 'var(--color-text-muted)', minWidth: 36 }}>Box {i+1}</span>
                  <input type="number" step="0.1" value={box.weight || ''} onChange={e => updateBox(i, 'weight', e.target.value)}
                    placeholder="kg" style={{ width: 50, padding: '3px 4px', fontSize: 11, borderRadius: 4, border: '1px solid var(--color-border)' }} />
                  <span style={{ color: 'var(--color-text-muted)' }}>kg</span>
                  <input type="number" value={box.length || ''} onChange={e => updateBox(i, 'length', e.target.value)}
                    placeholder="L" style={{ width: 42, padding: '3px 4px', fontSize: 11, borderRadius: 4, border: '1px solid var(--color-border)' }} />
                  <span style={{ color: 'var(--color-text-muted)' }}>×</span>
                  <input type="number" value={box.width || ''} onChange={e => updateBox(i, 'width', e.target.value)}
                    placeholder="W" style={{ width: 42, padding: '3px 4px', fontSize: 11, borderRadius: 4, border: '1px solid var(--color-border)' }} />
                  <span style={{ color: 'var(--color-text-muted)' }}>×</span>
                  <input type="number" value={box.height || ''} onChange={e => updateBox(i, 'height', e.target.value)}
                    placeholder="H" style={{ width: 42, padding: '3px 4px', fontSize: 11, borderRadius: 4, border: '1px solid var(--color-border)' }} />
                  <span style={{ color: 'var(--color-text-muted)', fontSize: 10 }}>cm</span>
                  {editBoxes.length > 1 && (
                    <button onClick={() => removeBox(i)} style={{ background: 'none', border: 'none', color: 'var(--color-error)', cursor: 'pointer', fontSize: 14, padding: '0 2px' }}>×</button>
                  )}
                </div>
              ))}
              <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                <button className="btn btn-xs" onClick={addBox}
                  style={{ fontSize: 10, padding: '3px 8px', background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)' }}>
                  + Add Box
                </button>
                <div style={{ flex: 1 }} />
                <button className="btn btn-xs" onClick={() => setEditingBoxes(false)}
                  style={{ fontSize: 10, padding: '3px 8px' }}>
                  Cancel
                </button>
                <button className="btn btn-xs" onClick={saveBoxes} disabled={savingBoxes}
                  style={{ fontSize: 10, padding: '3px 8px', background: '#333', color: 'white', border: 'none' }}>
                  {savingBoxes ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          ) : pkg ? (
            <>
              <div style={{ fontSize: 13, marginBottom: 8 }}>
                {pkg.boxes?.length || 1} box(es) · {formatNumber(pkg.totalWeight || 0)}kg total
              </div>
              {pkg.boxes?.map((box, i) => (
                <div key={i} style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                  Box {i+1}: {formatNumber(box.weight)}kg - {formatNumber(box.length)}×{formatNumber(box.width)}×{formatNumber(box.height)}cm
                </div>
              ))}
            </>
          ) : shipment.package_details ? (
            <div style={{
              background: 'var(--color-bg-secondary)',
              padding: 'var(--space-2)',
              borderRadius: 'var(--radius)',
              fontSize: 12,
              whiteSpace: 'pre-line',
              lineHeight: 1.8
            }}>
              {shipment.package_details.replace(/\s*(Box\s*\d+)/gi, '\n$1').trim()}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>No package details</div>
          )}
        </div>

        {/* Boxes: one card per box with its contents and label (Trac #1077, #1337) */}
        <CollectionParcels state={parcelsState} />

        {/* FedEx Actions */}
        {selectedCarrier === 'fedex' && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>FEDEX ACTIONS</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                className="btn btn-secondary"
                onClick={() => generateFedExCSV(shipment, contactInfo)}
                style={{ flex: 1, fontSize: 12, padding: '8px 12px' }}
              >
                Download Upload File
              </button>
              <OffOnStaging feature="carrier">
              <button
                className="btn btn-secondary"
                onClick={handleSchedulePickup}
                disabled={schedulingPickup || !!pickupResult || !trackingNumber || isOnHold}
                title={isOnHold ? 'Lift the hold to book' : undefined}
                style={{
                  flex: 1, fontSize: 12, padding: '8px 12px',
                  opacity: (schedulingPickup || pickupResult || !trackingNumber || isOnHold) ? 0.6 : 1,
                  cursor: (schedulingPickup || pickupResult || !trackingNumber || isOnHold) ? 'not-allowed' : 'pointer'
                }}
              >
                {schedulingPickup ? 'Scheduling...' : pickupResult ? 'Pickup Scheduled' : isOnHold ? 'On Hold' : !trackingNumber ? 'Enter Tracking First' : 'Schedule Pickup'}
              </button>
              </OffOnStaging>
            </div>
            {pickupResult && (
              <div style={{
                marginTop: 8, padding: '8px 12px', fontSize: 12,
                background: 'rgba(59, 178, 115, 0.1)', border: '1px solid var(--green-onions)',
                borderRadius: 'var(--radius)'
              }}>
                Confirmation: <strong>{pickupResult.confirmationCode}</strong>{pickupResult.locationCode ? <> ({pickupResult.locationCode})</> : null} · {pickupResult.collectionDate} · {pickupResult.pickupWindow}
              </div>
            )}
          </div>
        )}

        {/* Royal Mail Actions */}
        {isRoyalMail && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>ROYAL MAIL ACTIONS</div>
            {rmShipment ? (
              <div>
                <div style={{
                  padding: '8px 12px', fontSize: 12,
                  background: 'rgba(59, 178, 115, 0.1)', border: '1px solid var(--green-onions)',
                  borderRadius: 'var(--radius)', marginBottom: 8
                }}>
                  <div>Tracking: <strong>{rmShipment.tracking_number || 'Pending'}</strong></div>
                  <div style={{ marginTop: 4 }}>Order: <strong>{rmShipment.order_identifier}</strong></div>
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    try {
                      const res = await fetch(`/api/deals/collections/royal-mail/${shipment.id}/label`, { credentials: 'include' })
                      if (!res.ok) throw new Error('Label not available')
                      const blob = await res.blob()
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = `rm-label-${shipment.id}.pdf`
                      a.click()
                      URL.revokeObjectURL(url)
                    } catch (e) {
                      alert(`Label download failed: ${e.message}`)
                    }
                  }}
                  style={{ fontSize: 12, padding: '8px 12px' }}
                >
                  Download Label (PDF)
                </button>
              </div>
            ) : (
              <OffOnStaging feature="carrier">
              <button
                className="btn btn-secondary"
                onClick={async () => {
                  setRmCreating(true)
                  try {
                    const result = await api.post('/api/deals/collections/royal-mail/create-shipment', { dealId: shipment.id })
                    setRmShipment({
                      tracking_number: result.trackingNumber,
                      order_identifier: result.orderIdentifier
                    })
                    // The label exists either way, so this is not a failure —
                    // but the tracking number never reached the deal, which is
                    // where the customer portal and the warehouse read it from.
                    // Nobody was consuming this field, so the write failed
                    // silently to the operator even after the API started
                    // reporting it.
                    if (result.warning) {
                      alert(`Label created (${result.trackingNumber}), but:\n\n${result.warning}\n\nAdd the tracking number to the deal by hand.`)
                    }
                  } catch (e) {
                    alert(`Shipment creation failed: ${e.message}`)
                  } finally {
                    setRmCreating(false)
                  }
                }}
                disabled={rmCreating || isOnHold}
                title={isOnHold ? 'Lift the hold to book' : undefined}
                style={{
                  fontSize: 12, padding: '8px 12px',
                  background: '#E74C3C', color: 'white', border: 'none',
                  opacity: (rmCreating || isOnHold) ? 0.6 : 1,
                  cursor: (rmCreating || isOnHold) ? 'not-allowed' : 'pointer'
                }}
              >
                {rmCreating ? 'Creating Shipment...' : isOnHold ? 'On Hold — lift to book' : 'Create Royal Mail Shipment'}
              </button>
              </OffOnStaging>
            )}
          </div>
        )}

        {/* Label Upload - for FedEx or Drop-offs */}
        {needsLabelUpload && (
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>
              {isDropOff ? 'DROP-OFF LABEL' : 'FEDEX LABEL'} {boxCount > 1 ? `(${boxCount} BOXES)` : ''} <span style={{ color: 'var(--color-error)' }}>*</span>
            </div>

            {/* Uploaded label */}
            {uploadedLabelUrl && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                background: 'rgba(59, 178, 115, 0.1)',
                border: '1px solid var(--green-onions)',
                borderRadius: 'var(--radius)',
                marginBottom: 8
              }}>
                <span style={{ color: 'var(--green-onions)', fontSize: 16 }}>✓</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>
                  {isDropOff ? 'Drop-off Label' : 'FedEx Label'}{boxCount > 1 ? ` (${boxCount} boxes)` : ''}
                </span>
                <a
                  href={uploadedLabelUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: 'var(--color-info)', fontSize: 12, textDecoration: 'none' }}
                >
                  View
                </a>
                <button
                  onClick={() => setUploadedLabelUrl(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: 18 }}
                >
                  ×
                </button>
              </div>
            )}

            {/* Pending file */}
            {labelFile && !uploadedLabelUrl && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '12px 16px',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius)',
                marginBottom: 8
              }}>
                <span style={{ fontSize: 16 }}>📎</span>
                <span style={{ flex: 1, fontSize: 13 }}>{labelFile.name}</span>
                <button
                  onClick={() => setLabelFile(null)}
                  style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', fontSize: 18 }}
                >
                  ×
                </button>
              </div>
            )}

            {/* Upload button */}
            {labelFile && !uploadedLabelUrl && (
              <button
                className="btn btn-secondary"
                onClick={handleUploadLabel}
                disabled={isUploading}
                style={{ width: '100%', marginBottom: 8 }}
              >
                {isUploading ? 'Uploading...' : 'Upload Label'}
              </button>
            )}

            {/* File input dropzone */}
            {!uploadedLabelUrl && !labelFile && (
              <div style={{
                border: '2px dashed var(--color-border)',
                borderRadius: 'var(--radius)',
                padding: 24,
                textAlign: 'center',
                cursor: 'pointer',
                position: 'relative'
              }}>
                <input
                  type="file"
                  accept=".pdf,image/*"
                  onChange={handleFileSelect}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                />
                <div style={{ fontSize: 24, marginBottom: 8 }}>📎</div>
                <p style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                  Upload {isDropOff ? 'drop-off' : 'shipping'} label
                </p>
                <p style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                  {boxCount > 1 ? `PDF with all ${boxCount} box labels` : 'PDF or image'}
                </p>
              </div>
            )}

            {/* Upload error */}
            {uploadError && (
              <div style={{
                marginTop: 8,
                padding: 12,
                background: 'rgba(231, 76, 60, 0.1)',
                borderRadius: 'var(--radius)',
                color: 'var(--color-danger)',
                fontSize: 12
              }}>
                {uploadError}
              </div>
            )}
          </div>
        )}

        {/* Tracking & Cost */}
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>SHIPMENT DETAILS</div>
          <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
            <label style={{ fontSize: 12 }}>Tracking Number <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input
              type="text"
              className="form-input"
              value={trackingNumber}
              onChange={e => setTrackingNumber(extractTrackingNumber(e.target.value))}
              onPaste={e => {
                e.preventDefault()
                const pasted = e.clipboardData.getData('text')
                setTrackingNumber(extractTrackingNumber(pasted))
              }}
              placeholder="Enter or paste tracking number/URL"
            />
            {trackingNumber && (
              <div style={{ marginTop: 4 }}>
                {trackingNumber.split(',').map((tn, idx) => {
                  const trimmed = tn.trim()
                  if (!trimmed) return null
                  const url = getTrackingUrl(trimmed, selectedCarrier)
                  if (!url) return null
                  return (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: 11, color: 'var(--color-info)', display: 'block', marginTop: idx > 0 ? 2 : 0 }}
                    >
                      Track: {trimmed} →
                    </a>
                  )
                })}
              </div>
            )}
          </div>
          <div className="form-group" style={{ marginBottom: 'var(--space-3)' }}>
            <label style={{ fontSize: 12 }}>Shipping Cost (£) <span style={{ color: 'var(--color-error)' }}>*</span></label>
            <input
              type="number"
              step="0.01"
              className="form-input"
              value={shippingCost}
              onChange={e => setShippingCost(e.target.value)}
              placeholder="0.00"
            />
          </div>
          {!isDropOff && (
            <div className="form-group">
              <label style={{ fontSize: 12 }}>Collection Date</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="date"
                  className="form-input"
                  value={collectionDate}
                  onChange={e => setCollectionDate(e.target.value)}
                  min={minDate}
                  style={{ flex: 1 }}
                />
                {dateDirty && (
                  <button
                    className="btn btn-xs"
                    onClick={saveCollectionDate}
                    disabled={savingDate}
                    style={{
                      fontSize: 11,
                      padding: '8px 12px',
                      background: 'var(--green-onions, #3BB273)',
                      color: 'white',
                      border: 'none',
                      whiteSpace: 'nowrap',
                      cursor: savingDate ? 'not-allowed' : 'pointer',
                      opacity: savingDate ? 0.6 : 1
                    }}
                  >
                    {savingDate ? 'Saving...' : 'Save Date'}
                  </button>
                )}
                {dateSaved && !dateDirty && (
                  <span style={{ fontSize: 12, color: 'var(--green-onions, #3BB273)', whiteSpace: 'nowrap' }}>✓ Saved</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Items */}
        {shipment.parsed_items && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: 'var(--color-text-muted)' }}>ITEMS</div>
            <div style={{ background: 'var(--color-bg-secondary)', padding: 'var(--space-2)', borderRadius: 'var(--radius)', fontSize: 12 }}>
              {shipment.parsed_items.split('\n').filter(l => l.trim()).map((item, i) => (
                <div key={i} style={{ padding: '4px 0' }}>{item}</div>
              ))}
            </div>
          </div>
        )}

        {/* Cancel this collection (Trac #993) */}
        {cancelOpen && (
          <div ref={cancelSectionRef}>
            <CollectionCancelSection
              shipment={shipment}
              parcels={parcelsState.parcels}
              pickup={pickupResult}
              onKeep={() => setCancelOpen(false)}
              onCancelled={() => onCancelled?.(shipment.id)}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <div style={{ padding: 'var(--space-4)', borderTop: '1px solid var(--color-border)', display: 'flex', gap: 'var(--space-2)' }}>
        <button className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>
          Close
        </button>
        <button
          className="btn"
          onClick={openCancelSection}
          style={{ flex: 1, background: 'white', color: 'var(--color-error)', border: '1px solid var(--color-error)' }}
        >
          Cancel collection
        </button>
        {(() => {
          const missingFields = []
          if (!trackingNumber) missingFields.push('Tracking Number')
          if (!shippingCost) missingFields.push('Shipping Cost')
          if (needsLabelUpload && !uploadedLabelUrl) missingFields.push('Label Upload')
          // A held collection is protected from booking until the hold is lifted.
          const isDisabled = saving || isOnHold || missingFields.length > 0

          const handleClick = () => {
            if (isOnHold) return
            if (missingFields.length > 0) {
              alert(`Please enter the following required fields:\n\n• ${missingFields.join('\n• ')}`)
              return
            }
            handleConfirm()
          }

          return (
            <button
              className="btn btn-primary"
              onClick={handleClick}
              // Disable only for saving/held. A missing-field click must still
              // fire handleClick so the "what's missing" alert shows — a disabled
              // button swallows the click and leaves the operator with no clue.
              disabled={saving || isOnHold}
              title={isOnHold ? 'Lift the hold to book' : undefined}
              style={{
                flex: 1,
                opacity: isDisabled ? 0.4 : 1,
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                background: isDisabled ? 'var(--color-border)' : undefined
              }}
            >
              {saving ? 'Processing...' : isOnHold ? 'On Hold — lift to book' : 'Confirm & Book'}
            </button>
          )
        })()}
      </div>
    </div>
  )
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

function CourierBoard({ onCollectionCancelled }) {
  const [deals, setDeals] = useState([])
  // "Deliver in person" lane (spec_20260805) — its own view, never mixed into
  // the carrier grouping below (no address, no carrier to assign).
  const [warehouseDeals, setWarehouseDeals] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ pending: 0, scheduled: 0, problemCount: 0, bookedToday: 0, fedexBookedCount: 0 })
  const [selectedShipment, setSelectedShipment] = useState(null)
  const [generatingToken, setGeneratingToken] = useState(null)
  const [generatedLink, setGeneratedLink] = useState(null)

  // Track recently booked deal IDs to filter them out on reload
  const [recentlyBookedIds, setRecentlyBookedIds] = useState(new Set())

  // FedEx batch select
  const [batchMode, setBatchMode] = useState(false)
  const [selectedFedExIds, setSelectedFedExIds] = useState(new Set())
  const [rmBatchMode, setRmBatchMode] = useState(false)
  const [selectedRmIds, setSelectedRmIds] = useState(new Set())
  const [rmBatchLoading, setRmBatchLoading] = useState(false)

  // For clickable KPIs
  const [bookedTodayDeals, setBookedTodayDeals] = useState([])
  const [problemDeals, setProblemDeals] = useState([])
  const [fedexPickupDeals, setFedexPickupDeals] = useState([])
  const [fedexBookedPickups, setFedexBookedPickups] = useState([])
  const [resendingEmail, setResendingEmail] = useState(null)
  const [cancellingPickupFor, setCancellingPickupFor] = useState(null)
  const [showKpiModal, setShowKpiModal] = useState(null) // 'bookedToday', 'problem', 'fedexPickup', or 'fedexBooked'
  const [pickupDate, setPickupDate] = useState('')
  const [schedulingPickupFor, setSchedulingPickupFor] = useState(null)

  useEffect(() => {
    loadCollections()
  }, [])

  async function loadCollections() {
    setLoading(true)
    try {
      // Fetch pending, scheduled, problem, and fedex pickup deals
      // Use allSettled so one failing view doesn't break the entire page
      const results = await Promise.allSettled([
        api.get('/api/deals/collections?view=pending'),
        api.get('/api/deals/collections?view=scheduled'),
        api.get('/api/deals/collections?view=problem'),
        api.get('/api/deals/collections?view=fedex_pickup'),
        api.get('/api/deals/collections/fedex-pickups'),
        // "Deliver in person" (spec_20260805): its own view/queue, so booked
        // drop-offs never vanish into the view=pending blind spot.
        api.get('/api/deals/collections?view=warehouse_dropoff')
      ])
      const [pendingRes, scheduledRes, problemRes, fedexPickupRes, fedexBookedRes, warehouseRes] = results.map((r, i) => {
        if (r.status === 'fulfilled') return r.value
        console.warn(`Collections request ${i} failed:`, r.reason)
        return {}
      })
      setWarehouseDeals(warehouseRes.deals || [])

      // Combine pending deals for the main view, filtering out recently booked
      const pendingDeals = (pendingRes.deals || []).filter(d => !recentlyBookedIds.has(d.id))
      setDeals(pendingDeals)

      // Store problem deals and fedex pickup deals
      setProblemDeals(problemRes.deals || [])
      setFedexPickupDeals(fedexPickupRes.deals || [])
      setFedexBookedPickups(fedexBookedRes.pickups || [])

      // Calculate booked today from BOTH pending and scheduled deals
      const today = new Date()
      today.setHours(0, 0, 0, 0)

      const allDeals = [...(pendingRes.deals || []), ...(scheduledRes.deals || [])]

      const isBookedToday = (d) => {
        if (!d.collection_booked_date) return false

        const dateVal = d.collection_booked_date
        let bookedDate

        // Handle epoch timestamp (HubSpot often returns dates as milliseconds)
        if (typeof dateVal === 'number' || /^\d{13}$/.test(dateVal)) {
          bookedDate = new Date(parseInt(dateVal))
        } else if (/^\d{10}$/.test(dateVal)) {
          // Unix seconds
          bookedDate = new Date(parseInt(dateVal) * 1000)
        } else if (typeof dateVal === 'string') {
          if (dateVal.includes('T')) {
            // ISO format
            bookedDate = new Date(dateVal)
          } else if (dateVal.includes('/')) {
            // UK format DD/MM/YYYY
            const [day, month, year] = dateVal.split('/')
            bookedDate = new Date(year, month - 1, day)
          } else if (dateVal.includes('-')) {
            // YYYY-MM-DD
            bookedDate = new Date(dateVal)
          }
        }

        if (!bookedDate || isNaN(bookedDate.getTime())) return false

        // Compare dates (ignore time)
        bookedDate.setHours(0, 0, 0, 0)
        return bookedDate.getTime() === today.getTime()
      }

      const todayDeals = allDeals.filter(isBookedToday)
      setBookedTodayDeals(todayDeals)

      if (pendingRes.stats) {
        setStats({
          ...pendingRes.stats,
          bookedToday: todayDeals.length,
          problemCount: problemRes.deals?.length || 0,
          fedexPickupCount: fedexPickupRes.deals?.length || 0,
          fedexBookedCount: fedexBookedRes.pickups?.length || 0
        })
      }
    } catch (error) {
      console.error('Failed to load collections:', error)
    } finally {
      setLoading(false)
    }
  }

  async function resendLabelEmail(dealId) {
    setResendingEmail(dealId)
    try {
      const result = await api.post('/api/deals/collections/send-label-email', { dealId })
      if (result.emailSent) {
        alert('Email sent successfully!')
      } else {
        alert(`Failed to send email.${result.emailError ? `\n\nReason: ${result.emailError}` : ''}`)
      }
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setResendingEmail(null)
    }
  }

  async function cancelFedExPickup(dealId) {
    setCancellingPickupFor(dealId)
    try {
      const result = await api.post(`/api/deals/collections/${dealId}/cancel-pickup`, {})
      alert(`Pickup cancelled: ${result.message}`)
      setFedexBookedPickups(prev => prev.filter(p => p.id !== dealId))
      setFedexPickupDeals(prev => {
        if (prev.some(d => d.id === dealId)) return prev
        const pickup = fedexBookedPickups.find(p => p.id === dealId)
        if (!pickup) return prev
        return [...prev, pickup].sort((a, b) => {
          if (!a.collection_date) return 1
          if (!b.collection_date) return -1
          return new Date(a.collection_date) - new Date(b.collection_date)
        })
      })
      setStats(prev => ({
        ...prev,
        fedexBookedCount: Math.max(0, (prev.fedexBookedCount || 0) - 1),
        fedexPickupCount: (prev.fedexPickupCount || 0) + 1
      }))
    } catch (error) {
      alert(`Cancel pickup failed: ${error.message}`)
    } finally {
      setCancellingPickupFor(null)
    }
  }

  // The drawer stays open on its answer. The board drops the collection now
  // and the "Postage to cancel" tab reads again.
  function handleCollectionCancelled(dealId) {
    if (deals.some(d => d.id === dealId)) {
      setStats(prev => ({ ...prev, pending: Math.max(0, prev.pending - 1) }))
    }
    setDeals(prev => prev.filter(d => d.id !== dealId))
    setFedexPickupDeals(prev => prev.filter(d => d.id !== dealId))
    setFedexBookedPickups(prev => prev.filter(p => p.id !== dealId))
    onCollectionCancelled?.(dealId)
  }

  async function generateCollectionLink(dealId, contactId) {
    setGeneratingToken(dealId)
    try {
      const response = await api.post('/api/deals/collections/generate-token', { contactId, dealId })
      setGeneratedLink(response.formUrl)
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setGeneratingToken(null)
    }
  }

  function toggleFedExSelect(dealId) {
    setSelectedFedExIds(prev => {
      const next = new Set(prev)
      if (next.has(dealId)) next.delete(dealId)
      else next.add(dealId)
      return next
    })
  }

  function selectAllFedEx() {
    setSelectedFedExIds(new Set(shipmentsByCarrier.fedex.map(s => s.id)))
  }

  function deselectAllFedEx() {
    setSelectedFedExIds(new Set())
  }

  const [batchLoading, setBatchLoading] = useState(false)

  async function handleDownloadBatch() {
    const selected = shipmentsByCarrier.fedex.filter(s => selectedFedExIds.has(s.id))
    if (selected.length === 0) return

    setBatchLoading(true)
    try {
      // Fetch contact info for all selected deals
      const res = await api.post('/api/deals/collections/batch-contacts', {
        dealIds: selected.map(s => s.id)
      })
      const contacts = res.contacts || {}

      // Enrich shipments with contact data
      const enriched = selected.map(s => ({
        ...s,
        _contactName: contacts[s.id]?.name || '',
        _contactEmail: contacts[s.id]?.email || s.seller_email || ''
      }))

      generateBatchFedExCSV(enriched)
    } catch (err) {
      alert(`Failed to fetch contact details: ${err.message}`)
    } finally {
      setBatchLoading(false)
    }
  }

  function toggleRmSelect(dealId) {
    setSelectedRmIds(prev => {
      const next = new Set(prev)
      if (next.has(dealId)) next.delete(dealId)
      else next.add(dealId)
      return next
    })
  }

  async function handleRmBulkCreate() {
    const selected = shipmentsByCarrier.royal_mail.filter(s => selectedRmIds.has(s.id))
    if (selected.length === 0) return

    if (!confirm(`Create Royal Mail shipments for ${selected.length} deal(s)?`)) return

    setRmBatchLoading(true)
    try {
      const result = await api.post('/api/deals/collections/royal-mail/create-shipments', {
        dealIds: selected.map(s => s.id)
      })
      const created = result.created || 0
      const failed = result.failed || 0
      // On-Hold collections are skipped server-side (spec 20260911), never
      // booked wrong — surfaced so the operator knows why a selected deal did
      // not go out.
      const skipped = result.skipped || []
      // Per-deal warnings: the label was bought but its tracking number did not
      // reach the deal. Counted separately from `failed` because the shipment
      // itself succeeded — and named, because "3 need attention" without saying
      // which three is not actionable in a bulk run.
      const warned = (result.results || []).filter(r => r.warning)
      const summary = `Royal Mail bulk: ${created} created, ${failed} failed`
        + (skipped.length ? `, ${skipped.length} skipped (on hold)` : '')
      if (warned.length > 0) {
        alert(
          `${summary}\n\n⚠ ${warned.length} label(s) created but NOT recorded on the deal — `
          + `add the tracking number by hand:\n\n`
          + warned.map(r => `${r.dealId}: ${r.trackingNumber || 'no tracking'} — ${r.warning}`).join('\n\n')
        )
      } else {
        alert(summary)
      }
      setRmBatchMode(false)
      setSelectedRmIds(new Set())
      loadCollections()
    } catch (err) {
      alert(`Bulk shipment failed: ${err.message}`)
    } finally {
      setRmBatchLoading(false)
    }
  }

  // Group shipments by assigned carrier
  const shipmentsByCarrier = {
    royal_mail: [],
    parcelforce: [],
    fedex: [],
    shiply: []
  }

  const dealsWithAddress = deals.filter(d => d.collection_address)
  const dealsWithoutAddress = deals.filter(d => !d.collection_address)
  // On-Hold (spec 20260911): collections paused so they are not booked wrong.
  const onHoldDeals = deals.filter(d => d.collection_on_hold)

  // "Deliver in person" visits due TODAY and not yet received (the lane
  // self-clears on receive, so what's still here is still expected).
  const todayIso = new Date().toISOString().split('T')[0]
  const inPersonToday = warehouseDeals.filter(d => d.collection_date === todayIso)

  dealsWithAddress.forEach(deal => {
    // Use saved carrier from HubSpot if exists (case-insensitive), otherwise use auto-assigned
    const autoAssigned = assignCarrier(deal)
    const hubspotCarrier = deal.carrier?.toLowerCase()
    const carrierId = hubspotCarrier && CARRIERS[hubspotCarrier]
      ? hubspotCarrier
      : autoAssigned.carrierId
    if (shipmentsByCarrier[carrierId]) {
      shipmentsByCarrier[carrierId].push(deal)
    }
  })

  // Sort each carrier's shipments by collection_date (earliest first)
  Object.keys(shipmentsByCarrier).forEach(carrier => {
    shipmentsByCarrier[carrier].sort((a, b) => {
      if (!a.collection_date) return 1
      if (!b.collection_date) return -1
      return new Date(a.collection_date) - new Date(b.collection_date)
    })
  })

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}>
        <div className="loading-spinner" />
      </div>
    )
  }

  return (
    <>
      {/* Stats */}
      <div className="statsRow">
        <div
          className="statBox"
          style={{ cursor: stats.bookedToday > 0 ? 'pointer' : 'default' }}
          onClick={() => stats.bookedToday > 0 && setShowKpiModal('bookedToday')}
        >
          <div className="statValue">{stats.bookedToday || 0}</div>
          <div className="statLabel">Booked Today</div>
        </div>
        <div className="statBox active">
          <div className="statValue">{stats.pending}</div>
          <div className="statLabel">Pending Label</div>
        </div>
        <div
          className="statBox"
          style={{ cursor: stats.problemCount > 0 ? 'pointer' : 'default', background: stats.problemCount > 0 ? 'rgba(231, 76, 60, 0.1)' : undefined }}
          onClick={() => stats.problemCount > 0 && setShowKpiModal('problem')}
        >
          <div className="statValue" style={{ color: stats.problemCount > 0 ? 'var(--color-danger)' : undefined }}>{stats.problemCount || 0}</div>
          <div className="statLabel">Problem with Shipping</div>
        </div>
        <div
          className="statBox"
          style={{ cursor: stats.fedexPickupCount > 0 ? 'pointer' : 'default', background: stats.fedexPickupCount > 0 ? 'rgba(77, 20, 140, 0.1)' : undefined }}
          onClick={() => stats.fedexPickupCount > 0 && setShowKpiModal('fedexPickup')}
        >
          <div className="statValue" style={{ color: stats.fedexPickupCount > 0 ? '#4D148C' : undefined }}>{stats.fedexPickupCount || 0}</div>
          <div className="statLabel">Requires FedEx Pickup{(() => {
            const tomorrow = new Date()
            tomorrow.setDate(tomorrow.getDate() + 1)
            tomorrow.setHours(0, 0, 0, 0)
            const endOfTomorrow = new Date(tomorrow)
            endOfTomorrow.setDate(endOfTomorrow.getDate() + 1)
            return fedexPickupDeals.some(d => {
              if (!d.collection_date) return false
              const cd = new Date(d.collection_date + 'T00:00:00')
              return cd < endOfTomorrow
            }) ? ' *' : ''
          })()}</div>
        </div>
        <div
          className="statBox"
          style={{ cursor: stats.fedexBookedCount > 0 ? 'pointer' : 'default', background: stats.fedexBookedCount > 0 ? 'rgba(77, 20, 140, 0.18)' : undefined }}
          onClick={() => stats.fedexBookedCount > 0 && setShowKpiModal('fedexBooked')}
        >
          <div className="statValue" style={{ color: stats.fedexBookedCount > 0 ? '#4D148C' : undefined }}>{stats.fedexBookedCount || 0}</div>
          <div className="statLabel">Pickups Booked</div>
        </div>
        <div className="statBox">
          <div className="statValue">{dealsWithoutAddress.length}</div>
          <div className="statLabel">No Collection Link</div>
        </div>
        <div
          className="statBox"
          style={{ background: onHoldDeals.length > 0 ? 'rgba(184, 154, 46, 0.12)' : undefined }}
          title={onHoldDeals.length > 0 ? 'Collections paused — lift the hold before booking' : undefined}
        >
          <div className="statValue" style={{ color: onHoldDeals.length > 0 ? '#8a6d1f' : undefined }}>{onHoldDeals.length > 0 ? `⏸ ${onHoldDeals.length}` : 0}</div>
          <div className="statLabel">On Hold</div>
        </div>
        {/* Only rendered when someone is actually due: an always-on zero would
            train eyes to ignore it, and red is reserved for "act today". */}
        {inPersonToday.length > 0 && (
          <div className="statBox" style={{ border: '2px solid #E5625E' }}>
            <div className="statValue" style={{ color: '#E5625E' }}>⚠️ {inPersonToday.length}</div>
            <div className="statLabel">In Person Deliveries Expected Today</div>
          </div>
        )}
      </div>

      {/* Deals without collection address */}
      {dealsWithoutAddress.length > 0 && (
        <div className="card" style={{ marginTop: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
          <div style={{ padding: 'var(--space-3)', borderBottom: '1px solid var(--color-border)', fontWeight: 600 }}>
            Awaiting Collection Link ({dealsWithoutAddress.length})
          </div>
          <div style={{ padding: 'var(--space-3)', display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
            {dealsWithoutAddress.map(deal => (
              <div key={deal.id} style={{
                background: 'var(--color-bg-secondary)',
                padding: 'var(--space-2) var(--space-3)',
                borderRadius: 'var(--radius)',
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-2)'
              }}>
                <span style={{ fontSize: 13 }}>{deal.dealname || deal.id}</span>
                {deal.contact_id && (
                  <button
                    className="btn btn-xs btn-primary"
                    onClick={() => generateCollectionLink(deal.id, deal.contact_id)}
                    disabled={generatingToken === deal.id}
                  >
                    {generatingToken === deal.id ? '...' : 'Generate'}
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Carrier Swimlanes */}
      <div style={{
        display: 'flex',
        gap: 'var(--space-3)',
        overflowX: 'auto',
        paddingBottom: 'var(--space-4)'
      }}>
        <CarrierColumn
          carrierId="royal_mail"
          shipments={shipmentsByCarrier.royal_mail}
          selectedId={selectedShipment?.id}
          onSelect={setSelectedShipment}
          batchMode={rmBatchMode}
          batchLoading={rmBatchLoading}
          selectedBatchIds={selectedRmIds}
          onToggleBatch={() => { setRmBatchMode(b => !b); setSelectedRmIds(new Set()) }}
          onToggleSelect={toggleRmSelect}
          onSelectAll={() => setSelectedRmIds(new Set(shipmentsByCarrier.royal_mail.map(s => s.id)))}
          onDeselectAll={() => setSelectedRmIds(new Set())}
          onDownloadBatch={handleRmBulkCreate}
          batchActionLabel="Create Shipments"
        />
        <CarrierColumn
          carrierId="parcelforce"
          shipments={shipmentsByCarrier.parcelforce}
          selectedId={selectedShipment?.id}
          onSelect={setSelectedShipment}
        />
        <CarrierColumn
          carrierId="fedex"
          shipments={shipmentsByCarrier.fedex}
          selectedId={selectedShipment?.id}
          onSelect={setSelectedShipment}
          batchMode={batchMode}
          batchLoading={batchLoading}
          selectedFedExIds={selectedFedExIds}
          onToggleBatch={() => { setBatchMode(b => !b); setSelectedFedExIds(new Set()) }}
          onToggleSelect={toggleFedExSelect}
          onSelectAll={selectAllFedEx}
          onDeselectAll={deselectAllFedEx}
          onDownloadBatch={handleDownloadBatch}
          batchActionLabel="Download CSV"
        />
        <CarrierColumn
          carrierId="shiply"
          shipments={shipmentsByCarrier.shiply}
          selectedId={selectedShipment?.id}
          onSelect={setSelectedShipment}
        />
        <CarrierColumn
          carrierId="warehouse_dropoff"
          shipments={warehouseDeals}
          selectedId={selectedShipment?.id}
          onSelect={setSelectedShipment}
        />
      </div>

      {/* Detail Panel */}
      {selectedShipment && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.3)',
              zIndex: 999
            }}
            onClick={() => setSelectedShipment(null)}
          />
          {selectedShipment.send_type === 'warehouse_dropoff' ? (
            <WarehouseDropoffPanel
              shipment={selectedShipment}
              onClose={() => setSelectedShipment(null)}
              onRebooked={() => loadCollections()}
            />
          ) : (
          <DetailPanel
            shipment={selectedShipment}
            onClose={() => setSelectedShipment(null)}
            onCarrierSaved={(dealId, carrier) => {
              // Save-only: keep the deal on the board, just move it to the
              // saved carrier's column. No booking stats, no removal.
              setDeals(prev => prev.map(d => d.id === dealId ? { ...d, carrier } : d))
            }}
            onCancelled={handleCollectionCancelled}
            onHoldChanged={(dealId, onHold, reason) => {
              // Reflect the hold on the board immediately so the card badge
              // appears/clears without waiting for a reload.
              setDeals(prev => prev.map(d => d.id === dealId
                ? { ...d, collection_on_hold: onHold, collection_hold_reason: reason }
                : d))
            }}
            onMethodChanged={(dealId, sendType) => {
              setDeals(prev => prev.map(d => d.id === dealId ? { ...d, send_type: sendType } : d))
              // A drop-off in person has its own panel; a new object makes the
              // board re-pick the panel for the new method.
              setSelectedShipment(prev => (prev ? { ...prev } : prev))
              loadCollections()
            }}
            onConfirm={(bookedCarrier) => {
              // Optimistically remove the deal from local state immediately
              const bookedDeal = selectedShipment
              const bookedDealId = bookedDeal.id

              // Add to recently booked set to prevent it coming back on reload
              setRecentlyBookedIds(prev => new Set([...prev, bookedDealId]))

              // Clear from recently booked after 60 seconds (HubSpot should have updated by then)
              setTimeout(() => {
                setRecentlyBookedIds(prev => {
                  const next = new Set(prev)
                  next.delete(bookedDealId)
                  return next
                })
              }, 60000)

              setDeals(prev => prev.filter(d => d.id !== bookedDealId))
              // Keep the Booked Today list in sync with the count increment below,
              // so the KPI modal reflects deals booked during this session.
              const today = new Date().toISOString().split('T')[0]
              setBookedTodayDeals(prev => addBookedTodayDeal(prev, bookedDeal, bookedCarrier, today))
              setStats(prev => ({
                ...prev,
                pending: Math.max(0, prev.pending - 1),
                bookedToday: prev.bookedToday + 1
              }))
              setSelectedShipment(null)
              // No auto-reload - optimistic update handles UI, user can manually refresh if needed
            }}
          />
          )}
        </>
      )}

      {/* Generated Link Modal */}
      {generatedLink && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Collection Link Generated</h2>
              <button className="modal-close" onClick={() => setGeneratedLink(null)}>×</button>
            </div>
            <div className="modal-body-scroll">
              <p>Send this link to the customer:</p>
              <div style={{
                background: 'var(--color-bg-secondary)',
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius)',
                wordBreak: 'break-all',
                marginTop: 'var(--space-3)'
              }}>
                {generatedLink}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setGeneratedLink(null)}>Close</button>
              <button className="btn btn-primary" onClick={() => {
                navigator.clipboard.writeText(generatedLink)
                alert('Link copied!')
              }}>
                Copy Link
              </button>
            </div>
          </div>
        </div>
      )}

      {/* KPI Details Modal */}
      {showKpiModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: showKpiModal === 'fedexPickup' || showKpiModal === 'fedexBooked' ? 760 : 600 }}>
            <div className="modal-header">
              <h2>{showKpiModal === 'bookedToday' ? 'Booked Today' : showKpiModal === 'fedexPickup' ? 'Requires FedEx Pickup' : showKpiModal === 'fedexBooked' ? 'FedEx Pickups Booked' : 'Problem with Shipping'}</h2>
              <button className="modal-close" onClick={() => { setShowKpiModal(null); setSchedulingPickupFor(null) }}>×</button>
            </div>
            <div className="modal-body-scroll" style={{ maxHeight: 400 }}>
              {(() => {
                const modalDeals = showKpiModal === 'bookedToday' ? bookedTodayDeals
                  : showKpiModal === 'fedexPickup' ? fedexPickupDeals
                  : showKpiModal === 'fedexBooked' ? fedexBookedPickups
                  : problemDeals
                if (modalDeals.length === 0) {
                  return (
                    <div style={{ textAlign: 'center', padding: 'var(--space-6)', color: 'var(--color-text-muted)' }}>
                      No deals found
                    </div>
                  )
                }
                return (
                  <table className="table" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th>Deal ID</th>
                        <th>Name</th>
                        <th>Collection Date</th>
                        {showKpiModal === 'fedexBooked' && <th>FedEx Ref</th>}
                        {showKpiModal !== 'fedexPickup' && <th>Carrier</th>}
                        {showKpiModal === 'bookedToday' && <th>Email</th>}
                        {showKpiModal === 'fedexPickup' && <th>Pickup</th>}
                        {showKpiModal === 'fedexBooked' && <th>Action</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {modalDeals.map(deal => {
                        const autoCarrier = assignCarrier(deal)
                        const hubspotCarrier = deal.carrier?.toLowerCase()
                        const carrierId = hubspotCarrier && CARRIERS[hubspotCarrier] ? hubspotCarrier : autoCarrier.carrierId
                        const carrier = CARRIERS[carrierId]
                        return (
                          <tr
                            key={deal.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (showKpiModal !== 'fedexPickup') {
                                setShowKpiModal(null)
                                setSelectedShipment(deal)
                              }
                            }}
                          >
                            <td style={{ fontFamily: 'monospace' }}><DealLink deal={deal} /></td>
                            <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {deal.dealname || '-'}
                            </td>
                            <td>{deal.collection_date || '-'}</td>
                            {showKpiModal === 'fedexBooked' && (
                              <td style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                {deal.confirmation_code || '-'}{deal.location_code ? ` (${deal.location_code})` : ''}
                              </td>
                            )}
                            {showKpiModal !== 'fedexPickup' && (
                              <td>
                                <span style={{
                                  background: carrier?.color || '#999',
                                  color: 'white',
                                  padding: '2px 8px',
                                  borderRadius: 4,
                                  fontSize: 11
                                }}>
                                  {carrier?.name || 'Unknown'}
                                </span>
                              </td>
                            )}
                            {showKpiModal === 'bookedToday' && (
                              <td>
                                <OffOnStaging feature="email">
                                <button
                                  className="btn btn-xs"
                                  style={{ fontSize: 11, whiteSpace: 'nowrap' }}
                                  disabled={resendingEmail === deal.id}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    if (confirm(`Resend label email for ${deal.dealname || deal.id}?`)) {
                                      resendLabelEmail(deal.id)
                                    }
                                  }}
                                >
                                  {resendingEmail === deal.id ? 'Sending...' : 'Resend Email'}
                                </button>
                                </OffOnStaging>
                              </td>
                            )}
                            {showKpiModal === 'fedexPickup' && (
                              <td onClick={e => e.stopPropagation()}>
                                {schedulingPickupFor === deal.id ? (
                                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                                    <input
                                      type="date"
                                      value={pickupDate}
                                      onChange={e => setPickupDate(e.target.value)}
                                      style={{ fontSize: 11, padding: '2px 4px', width: 120 }}
                                      min={new Date().toISOString().split('T')[0]}
                                    />
                                    <button
                                      className="btn btn-xs"
                                      style={{ fontSize: 11, background: '#4D148C', color: 'white', border: 'none' }}
                                      disabled={!pickupDate || schedulingPickupFor === `${deal.id}_loading`}
                                      onClick={async () => {
                                        setSchedulingPickupFor(`${deal.id}_loading`)
                                        try {
                                          const addr = resolvedAddressFields(deal)
                                          const postcode = addr.postcode
                                          const pkg = parsePackageDetails(deal.package_details)
                                          const result = await api.post(`/api/deals/collections/${deal.id}/schedule-pickup`, {
                                            collectionDate: pickupDate,
                                            trackingNumber: deal.tracking_number || '',
                                            address: { line1: addr.line1, line2: addr.line2 },
                                            contactName: deal.customer_name || 'Customer',
                                            phone: deal.collection_phone || '',
                                            postcode: postcode || '',
                                            city: addr.city || '',
                                            totalWeight: pkg?.totalWeight || 0,
                                            packageCount: pkg?.boxes?.length || 1
                                          })
                                          // Remove from list on success
                                          setFedexPickupDeals(prev => prev.filter(d => d.id !== deal.id))
                                          setStats(prev => ({ ...prev, fedexPickupCount: (prev.fedexPickupCount || 1) - 1 }))
                                          setSchedulingPickupFor(null)
                                          setPickupDate('')
                                          alert(`Pickup scheduled! Confirmation: ${result.confirmationCode}\nDate: ${result.collectionDate}\nWindow: ${result.pickupWindow}`)
                                        } catch (error) {
                                          setSchedulingPickupFor(deal.id)
                                          alert(`Pickup scheduling failed: ${error.message}`)
                                        }
                                      }}
                                    >
                                      {schedulingPickupFor === `${deal.id}_loading` ? '...' : 'Go'}
                                    </button>
                                    <button
                                      className="btn btn-xs"
                                      style={{ fontSize: 11 }}
                                      onClick={() => { setSchedulingPickupFor(null); setPickupDate('') }}
                                    >
                                      ×
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="btn btn-xs"
                                    style={{ fontSize: 11, background: '#4D148C', color: 'white', border: 'none' }}
                                    onClick={() => {
                                      setSchedulingPickupFor(deal.id)
                                      setPickupDate(deal.collection_date || '')
                                    }}
                                  >
                                    Schedule Pickup
                                  </button>
                                )}
                              </td>
                            )}
                            {showKpiModal === 'fedexBooked' && (
                              <td onClick={e => e.stopPropagation()}>
                                <button
                                  className="btn btn-xs"
                                  style={{ fontSize: 11 }}
                                  disabled={cancellingPickupFor === deal.id}
                                  onClick={() => {
                                    if (confirm(`Cancel FedEx pickup ${deal.confirmation_code}${deal.location_code ? ` (${deal.location_code})` : ''}?`)) {
                                      cancelFedExPickup(deal.id)
                                    }
                                  }}
                                >
                                  {cancellingPickupFor === deal.id ? 'Cancelling...' : 'Cancel Pickup'}
                                </button>
                              </td>
                            )}
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => { setShowKpiModal(null); setSchedulingPickupFor(null) }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// The page has two tabs. "Courier board" is the board as it always was;
// "Customer changes" lists customers' requests to move a booked collection
// (spec 20261013 §12.3). Both stay mounted, so switching keeps the board's
// state and the badge counts open requests before the tab is opened.
const TABS = [
  { id: 'board', label: 'Courier board' },
  { id: 'changes', label: 'Customer changes', badgeTestId: 'customer-changes-badge' },
  { id: 'postage', label: 'Postage to cancel', badgeTestId: 'postage-badge' },
]

//: Reset a <button> to look like the app's link-style .tab, at 44px (A10).
const TAB_BUTTON = {
  background: 'none', borderTop: 0, borderLeft: 0, borderRight: 0,
  font: 'inherit', cursor: 'pointer', minHeight: 44,
}

// The tab's count of things waiting, or null when there is none.
function tabBadge(tabId, counts) {
  const count = counts[tabId]
  return count > 0 ? count : null
}

function Collections() {
  const [activeTab, setActiveTab] = useState('board')
  const [openChanges, setOpenChanges] = useState(0)
  const [openPostage, setOpenPostage] = useState(0)
  const [postageReloads, setPostageReloads] = useState(0)

  function onTabKeyDown(e) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return
    const at = TABS.findIndex((t) => t.id === activeTab)
    const next = TABS[(at + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length]
    setActiveTab(next.id)
    document.getElementById(`collections-tab-${next.id}`)?.focus()
  }

  return (
    <>
      <div className="tabs" role="tablist" aria-label="Collections" onKeyDown={onTabKeyDown}>
        {TABS.map((tab) => {
          const selected = activeTab === tab.id
          const badge = tabBadge(tab.id, { changes: openChanges, postage: openPostage })
          return (
            <button
              key={tab.id}
              id={`collections-tab-${tab.id}`}
              role="tab"
              aria-selected={selected}
              aria-controls={`collections-panel-${tab.id}`}
              aria-label={badge ? `${tab.label}, ${badge} waiting` : undefined}
              tabIndex={selected ? 0 : -1}
              className={`tab ${selected ? 'active' : ''}`}
              style={TAB_BUTTON}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {badge && (
                <span data-testid={tab.badgeTestId} className="badge badge-warning" style={{ marginLeft: 'var(--space-2)' }}>
                  {badge}
                </span>
              )}
            </button>
          )
        })}
      </div>
      <div id="collections-panel-board" role="tabpanel" aria-labelledby="collections-tab-board" hidden={activeTab !== 'board'}>
        <CourierBoard onCollectionCancelled={() => setPostageReloads(n => n + 1)} />
      </div>
      <div id="collections-panel-changes" role="tabpanel" aria-labelledby="collections-tab-changes" hidden={activeTab !== 'changes'}>
        <CustomerChangesPanel onCountChange={setOpenChanges} />
      </div>
      <div id="collections-panel-postage" role="tabpanel" aria-labelledby="collections-tab-postage" hidden={activeTab !== 'postage'}>
        <PostageToCancel reloadToken={postageReloads} onCountChange={setOpenPostage} />
      </div>
    </>
  )
}

export default Collections
